"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFetch = exports.ContentTypes = void 0;
const react_1 = require("react");
const ui_1 = require("@relymd/ui");
const contracts_1 = require("@relymd/contracts");
const auth_1 = require("./auth");
const parseResponseData = (res) => {
    return new Promise((resolve, reject) => {
        res
            .json()
            .then((result) => resolve(result))
            .catch((err) => reject(err));
    });
};
exports.ContentTypes = {
    JSON: 'application/json',
    MULTIPART_FORMDATA: 'multipart/form-data',
};
const AllowedContentTypes = [exports.ContentTypes.JSON, exports.ContentTypes.MULTIPART_FORMDATA];
const useFetch = (props, dependencies) => {
    const { _csrf } = (0, auth_1.useAuth)();
    // REF DEPENDENCIES FOR DETERMINING RE-RENDERS
    // --------
    // This list tracks "dependencies" for the "stateRef" reference.
    // If any of the properties (isLoading, isOk, etc...) is accessed
    // by the consumer of this hook, it will change the property below
    // to "true". Since all "state" in this hook is in refs, we determine
    // if we should re-render by using this.
    const stateRefDeps = (0, react_1.useRef)({
        isLoading: false,
        isComplete: false,
        isCanceled: false,
        isOk: false,
        data: false,
        error: false,
        response: false,
    });
    // Use "dispatchState()" method below to update the values
    // for this reference.
    const stateRef = (0, react_1.useRef)({
        isLoading: false,
        isComplete: false,
        isCanceled: false,
        isOk: false,
        abortController: global.document ? new AbortController() : null,
    });
    // SEE (REF DEPENDENCIES FOR DETERMINING RE-RENDERS) ABOVE
    const configRefDeps = (0, react_1.useRef)({
        request: false,
        body: false,
        headers: false,
        method: false,
    });
    // Use "dispatchConfig()" method below to update the values
    // for this reference.
    const configRef = (0, react_1.useRef)({
        request: props.request,
        body: props.body,
        headers: props.headers,
        method: props.method,
    });
    const unmountedRef = (0, react_1.useRef)(false);
    const initialMountedRef = (0, react_1.useRef)(false);
    const rerender = (0, react_1.useState)(null)[1];
    // Use this to update the "stateRef" reference. It will update the
    // values on the reference and also determine if a re-render is necessary
    // by comparing the list of dependencies.
    let dispatchState = (0, react_1.useCallback)((payload) => {
        let shouldUpdateState = false;
        const payloadKeys = Object.keys(payload);
        for (let i = 0; i < payloadKeys.length; i++) {
            const key = payloadKeys[i];
            if (stateRef.current[key] === payload[key]) {
                continue;
            }
            stateRef.current[key] = payload[key];
            if (stateRefDeps.current[key]) {
                shouldUpdateState = true;
            }
        }
        if (shouldUpdateState) {
            if (unmountedRef.current || !initialMountedRef.current) {
                return;
            }
            rerender({});
        }
    }, []);
    // Use this to update the "stateConfig" reference. It will update the
    // values on the reference and also determine if a re-render is necessary
    // by comparing the list of dependencies.
    const dispatchConfig = (0, react_1.useCallback)((payload) => {
        let shouldUpdateState = false;
        const payloadKeys = Object.keys(payload);
        for (let i = 0; i < payloadKeys.length; i++) {
            const key = payloadKeys[i];
            if (configRef.current[key] === payload[key]) {
                continue;
            }
            configRef.current[key] = payload[key];
            if (configRefDeps.current[key]) {
                shouldUpdateState = true;
            }
        }
        if (shouldUpdateState) {
            if (unmountedRef.current || !initialMountedRef.current) {
                return;
            }
            rerender({});
        }
    }, []);
    // Set the body of the request.
    //
    // Note: This will try to automatically add the CSRF token
    // to the request.
    //
    // -- EXPOSED TO CONSUMERS OF THIS HOOK
    const setBody = (0, react_1.useCallback)((body) => {
        let bodyTemp;
        if (body) {
            if (global.document && (0, ui_1.isBodyInit)(body)) {
                if ((0, ui_1.isFormData)(body) && !body.has('_csrf')) {
                    body.append('_csrf', _csrf);
                }
                bodyTemp = body;
            }
            else {
                // if (!Object.prototype.hasOwnProperty.call(body, '_csrf')) {
                //   (body as Record<string, unknown>)._csrf = _csrf;
                // }
                bodyTemp = JSON.stringify(body);
            }
        }
        else {
            bodyTemp = JSON.stringify({
                _csrf,
            });
        }
        dispatchConfig({
            body: bodyTemp,
        });
    }, []);
    // Set the headers of the request.
    //
    // -- EXPOSED TO CONSUMERS OF THIS HOOK
    const setHeaders = (0, react_1.useCallback)((headers) => {
        dispatchConfig({
            headers,
        });
    }, []);
    // Internal method used to automatically attempt to add the appropriate
    // headers to the request.
    const prepareHeaders = (0, react_1.useCallback)(() => {
        const defaultContentType = exports.ContentTypes.JSON;
        let currentHeaders = configRef.current.headers;
        if (currentHeaders) {
            const contentType = currentHeaders.get('Content-Type') || defaultContentType;
            if (!AllowedContentTypes.includes(contentType)) {
                throw new Error(`Unable to transform body into [${contentType}]. Allowed content types: [${AllowedContentTypes.join(', ')}]`);
            }
            if (!currentHeaders.has('Content-Type')) {
                currentHeaders.append('Content-Type', contentType);
                // dispatchConfig({
                //   headers: currentHeaders,
                // });
            }
        }
        else {
            const headers = new Headers();
            headers.append('Content-Type', defaultContentType);
            currentHeaders = headers;
        }
        if ((0, ui_1.isFormData)(configRef.current.body)) {
            // Form data indicates that the content type will be "multipart/form-data".
            // This content type needs a boundary. Let's remove the content type
            // so the browser will create it automatically for us.
            currentHeaders.delete('Content-Type');
        }
        if (!currentHeaders.has('Accept')) {
            currentHeaders.append('Accept', 'application/json');
        }
        else {
            currentHeaders.set('Accept', 'application/json');
        }
        dispatchConfig({
            headers: currentHeaders,
        });
    }, []);
    // Cancel the current request.
    //
    // -- EXPOSED TO CONSUMERS OF THIS HOOK
    const executeCancel = (0, react_1.useCallback)(() => {
        var _a, _b;
        (_a = stateRef.current.abortController) === null || _a === void 0 ? void 0 : _a.abort();
        stateRef.current.abortController = global.document ? new AbortController() : null;
        stateRef.current.signal = (_b = stateRef.current.abortController) === null || _b === void 0 ? void 0 : _b.signal;
    }, []);
    // Execute the request.
    //
    // -- EXPOSED TO CONSUMERS OF THIS HOOK
    const executeFetch = (0, react_1.useCallback)(() => {
        var _a, _b;
        // If the consumer of this hook passed in dependencies,
        // we check to see if any of them are "undefined."
        // If they are, we short circuit the fetch call.
        if (dependencies && dependencies.filter((item) => item === undefined).length > 0) {
            return;
        }
        // If we're un-mounted, return for a no-op.
        if (unmountedRef.current) {
            return;
        }
        // If there is already a request in progress, cancel it.
        if (stateRef.current.isLoading) {
            executeCancel();
        }
        dispatchState({
            isLoading: true,
            isCanceled: false,
            isComplete: false,
            isOk: false,
            error: undefined,
        });
        const fetchOptions = {
            body: configRef.current.body,
            headers: configRef.current.headers,
            method: configRef.current.method,
            signal: (_a = stateRef.current.abortController) === null || _a === void 0 ? void 0 : _a.signal,
        };
        // if (typeof configRef.current.request === 'string') {
        //   const parsedUrl = new URL(configRef.current.request);
        //   if (parsedUrl.protocol === 'https:' && parsedUrl.hostname.endsWith('.localhost')) {
        //     // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //     // @ts-ignore
        //     fetchOptions.agent = new https.Agent({
        //       rejectUnauthorized: false,
        //     });
        //   }
        // }
        if (((_b = fetchOptions.method) === null || _b === void 0 ? void 0 : _b.toLowerCase()) === 'get') {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            delete fetchOptions.body;
        }
        // eslint-disable-next-line consistent-return
        return new Promise((resolve, reject) => {
            fetch(configRef.current.request, fetchOptions)
                .then((res) => {
                if (!res.ok) {
                    const err = new contracts_1.ApiError();
                    parseResponseData(res)
                        .then((result) => {
                        if (result) {
                            err.data = result;
                            dispatchState({
                                isLoading: false,
                                isCanceled: false,
                                isComplete: true,
                                error: err,
                                response: res,
                            });
                            reject(err);
                        }
                        else {
                            err.message = 'An unexpected error occurred.';
                            dispatchState({
                                isLoading: false,
                                isCanceled: false,
                                isComplete: true,
                                error: err,
                                response: res,
                            });
                            reject(err);
                        }
                    })
                        .catch((_) => reject(_));
                }
                else {
                    parseResponseData(res)
                        .then((result) => {
                        dispatchState({
                            isLoading: false,
                            isCanceled: false,
                            isComplete: true,
                            isOk: true,
                            response: res,
                            data: result,
                        });
                        resolve(result);
                    })
                        .catch((_) => reject(_));
                }
            })
                .catch((err) => {
                var _a;
                console.error(err);
                if ((_a = stateRef.current.signal) === null || _a === void 0 ? void 0 : _a.aborted) {
                    dispatchState({
                        isLoading: false,
                        isCanceled: true,
                        isComplete: false,
                    });
                }
                else {
                    dispatchState({
                        isLoading: false,
                        isCanceled: false,
                        isComplete: true,
                        error: err,
                    });
                }
                reject(err);
            });
        });
    }, [dependencies]);
    // The return state of the hook.
    // Using "useMemo()" here since all state
    // of the hook is refs.
    const memoizedState = (0, react_1.useMemo)(() => {
        const state = {
            fetch: executeFetch,
            cancel: executeCancel,
            setBody,
            setHeaders,
        };
        Object.defineProperties(state, {
            isOk: {
                get: () => {
                    stateRefDeps.current.isOk = true;
                    return stateRef.current.isOk;
                },
                enumerable: true,
            },
            isLoading: {
                get: () => {
                    stateRefDeps.current.isLoading = true;
                    return stateRef.current.isLoading;
                },
                enumerable: true,
            },
            isComplete: {
                get: () => {
                    stateRefDeps.current.isComplete = true;
                    return stateRef.current.isComplete;
                },
                enumerable: true,
            },
            isCanceled: {
                get: () => {
                    stateRefDeps.current.isCanceled = true;
                    return stateRef.current.isCanceled;
                },
                enumerable: true,
            },
            error: {
                get: () => {
                    stateRefDeps.current.error = true;
                    return stateRef.current.error;
                },
                enumerable: true,
            },
            data: {
                get: () => {
                    stateRefDeps.current.data = true;
                    return stateRef.current.data;
                },
                enumerable: true,
            },
            response: {
                get: () => {
                    stateRefDeps.current.response = true;
                    return stateRef.current.response;
                },
                enumerable: true,
            },
        });
        return state;
    }, [dependencies]);
    // We're mounted. Do initial setup.
    (0, ui_1.useIsomorphicLayoutEffect)(() => {
        unmountedRef.current = false;
        initialMountedRef.current = true;
        return () => {
            dispatchState = () => null;
            unmountedRef.current = true;
        };
    }, []);
    // When body props change
    // update our internal body ref.
    (0, react_1.useEffect)(() => {
        setBody(props.body);
        prepareHeaders();
    }, [props.body]);
    // When request prop changes
    // update our internal request ref.
    (0, react_1.useEffect)(() => {
        dispatchConfig({
            request: props.request,
        });
    }, [props.request]);
    // When method prop changes
    // update our internal method ref.
    (0, react_1.useEffect)(() => {
        dispatchConfig({
            method: props.method,
        });
    }, [props.method]);
    return memoizedState;
};
exports.useFetch = useFetch;
//# sourceMappingURL=fetch.js.map